import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { supabase } from '../services/supabase';
import { format } from 'date-fns';

// emergency_notice 는 항상 id=1 단일 행
const ROW_ID = 1;

function EmergencyNotice() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    title: '서버 점검 안내',
    message: '현재 서버 점검 중입니다.\n잠시 후 다시 이용해 주세요.',
    button_text: '확인',
  });

  useEffect(() => {
    fetchNotice();
  }, []);

  const fetchNotice = async () => {
    try {
      const { data, error } = await supabase
        .from('emergency_notice')
        .select('*')
        .eq('id', ROW_ID)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setForm({
          enabled: data.enabled ?? false,
          title: data.title ?? '',
          message: data.message ?? '',
          button_text: data.button_text ?? '확인',
        });
        setUpdatedAt(data.updated_at);
      }
    } catch (e) {
      console.error('Failed to fetch emergency_notice:', e);
      setError('긴급 점검 설정을 불러오는데 실패했습니다. (테이블이 없으면 마이그레이션을 먼저 실행하세요)');
    } finally {
      setLoading(false);
    }
  };

  const save = async (overrides = {}) => {
    const payload = { ...form, ...overrides };
    if (!payload.title || !payload.message) {
      setError('제목과 내용을 입력하세요.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { error } = await supabase
        .from('emergency_notice')
        .update({
          enabled: payload.enabled,
          title: payload.title,
          message: payload.message,
          button_text: payload.button_text || '확인',
        })
        .eq('id', ROW_ID);

      if (error) throw error;

      setForm(payload);
      setSuccess(
        payload.enabled
          ? '⚠️ 긴급 점검 팝업이 켜졌습니다. 사용자 앱에 점검 팝업이 표시됩니다.'
          : '긴급 점검 팝업이 꺼졌습니다.'
      );
      fetchNotice();
    } catch (e) {
      console.error('Failed to save emergency_notice:', e);
      setError('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 토글은 즉시 저장(긴급 상황 빠른 ON/OFF)
  const handleToggle = (e) => {
    save({ enabled: e.target.checked });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">긴급 점검 팝업</Typography>
        <Chip
          icon={<WarningIcon />}
          label={form.enabled ? '점검 중 (팝업 ON)' : '정상 (팝업 OFF)'}
          color={form.enabled ? 'error' : 'default'}
          variant={form.enabled ? 'filled' : 'outlined'}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity={form.enabled ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3, border: form.enabled ? '2px solid' : '1px solid', borderColor: form.enabled ? 'error.main' : 'divider' }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={handleToggle}
                color="error"
                disabled={saving}
              />
            }
            label={
              <Typography variant="h6">
                긴급 점검 팝업 {form.enabled ? '켜짐' : '꺼짐'}
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            켜면 사용자 앱 시작/복귀 시 닫을 수 없는 점검 팝업이 뜨고, "확인"을 누르면 앱이 종료됩니다.
            토글은 즉시 반영됩니다. (이미 실행 중인 사용자는 다음 포그라운드 복귀 시 적용)
          </Typography>
          {updatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              마지막 변경: {format(new Date(updatedAt), 'yyyy-MM-dd HH:mm:ss')}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>팝업 문구</Typography>
          <TextField
            fullWidth
            label="제목"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="내용"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            margin="normal"
            multiline
            rows={5}
            required
            helperText="줄바꿈은 그대로 앱에 반영됩니다."
          />
          <TextField
            fullWidth
            label="버튼 문구"
            value={form.button_text}
            onChange={(e) => setForm({ ...form, button_text: e.target.value })}
            margin="normal"
            placeholder="확인"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={() => save()} disabled={saving}>
              {saving ? '저장 중...' : '문구 저장'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default EmergencyNotice;
